// /src/pages/LoginDiscordMfa.jsx

// English comments only in code blocks
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import discordIcon from '../../src/medias/discord-white.png';
import { preloadUser } from '../../src/preloadUser';
import { useAuth } from '../authContext';

export default function LoginDiscordMfa() {
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [discordNameLocal, setDiscordNameLocal] = useState('');
  const [discordAvatarLocal, setDiscordAvatarLocal] = useState('');
  const [userId, setUserId] = useState(null);
  const [tempToken, setTempToken] = useState(null);
  const [mfaType, setMfaType] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    setIsLoggedIn,
    setEmail: setAuthEmail,
    setDiscordName,
    setDiscordId,
    setHasMfa,
    setHandles,
    setLanguage,
    setTimezone,
    setLastAcceptanceDate,
    setGcuAccepted,
  } = useAuth();

  // English comment: Extract URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setUserId(params.get('userId'));
    setTempToken(params.get('tempToken'));
    setMfaType(params.get('mfaType'));
    setRememberMe(params.get('rememberMe') === 'true');
    setDiscordNameLocal(decodeURIComponent(params.get('discordName')));
    setDiscordAvatarLocal(decodeURIComponent(params.get('discordAvatar')));
  }, [location]);

  const handleMfaCodeChange = (e) => {
    setMfaCode(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMfaError('');

    try {
      const response = await axios.post('/api/verify-mfa-discord', {
        userId,
        tempToken,
        mfaCode,
        mfaType,
        rememberMe,
      });

      if (response.data.success) {

        // English comment: Load user data after MFA validation.
        const fullUser = await preloadUser({ isLoggedIn: true });

        if (!fullUser) {
          console.error('Error: preloadUser returned null.');
          return;
        }

        // English comment: Update global AuthContext.
        setIsLoggedIn(true);
        setAuthEmail(fullUser.email ?? null);
        setDiscordName(fullUser.discordName ?? null);
        setDiscordId(fullUser.discordId ?? null);
        setHasMfa(fullUser.hasMfa ?? false);
        setHandles(fullUser.handles ?? null);
        setLanguage(fullUser.language ?? null);
        setTimezone(fullUser.timezone ?? null);
        setLastAcceptanceDate(fullUser.lastAcceptanceDate ?? null);
        setGcuAccepted(fullUser.gcuAccepted ?? false);

        // English comment: Redirection after login.
        const initialPath = sessionStorage.getItem('initialPath');
        if (initialPath) {
          sessionStorage.removeItem('initialPath');
          navigate(initialPath, { replace: true });
        } else {
          navigate('/members/personalhub', { replace: true });
        }

      } else {
        setMfaError(response.data.message);
      }

    } catch (error) {
      console.error('Error verifying MFA:', error);
      setMfaError('An error occurred while verifying MFA. Please try again.');
    }
  };

  return (
    <div className="login-container">
      <Form className="login-card login-card--discord-mfa" onSubmit={handleSubmit}>
        <div className="login-tab-discord identity-check">
          Enter your Pallas-Invictus MFA code
        </div>

        <div className="discord-login identity-check discord-mfa">
          <div className="login-discord-container">
            <Button className="discord-button discord-mfa">
              <div className="icon-container icon-discord">
                <img
                  src={discordAvatarLocal}
                  alt={discordIcon}
                  className="icon-image linked"
                />
              </div>
              <label className="discord-label">
                {discordNameLocal}
              </label>
            </Button>
          </div>
        </div>

        <div className="custom-field mfa-field">
          <Form.Group controlId="formMfaCode">
            <Form.Control
              className="mfa-field"
              type="text"
              placeholder="______"
              required
              value={mfaCode}
              onChange={handleMfaCodeChange}
            />
          </Form.Group>

          <div className="mfa-description">
            {mfaType === 'email' && (
              <p>An MFA code has been sent to you by email.</p>
            )}
            {mfaType === 'app' && (
              <p>Please enter the code from your authenticator app.</p>
            )}
          </div>
        </div>

        <Button variant="primary" type="submit" className="custom-button mfa-button">
          {t('Submit')}
        </Button>

        {mfaError && <div className="error-message">{mfaError}</div>}
      </Form>
    </div>
  );
}
